// Import real API implementations from separate files
import { UserAPI as UserAPIImpl } from './user-api-fetch'
import { PhotoAPI as PhotoAPIImpl } from './photo-api-fetch'
import { AlbumAPI as AlbumAPIImpl } from './album-api-fetch'
import { FavoriteAPI as FavoriteAPIImpl } from './favorite-api-fetch'
import { ShareAPI as ShareAPIImpl } from './share-api-fetch'
import { DMAPI as DMAPIImpl } from './dm-api-fetch'
import { SettingAPI as SettingAPIImpl } from './setting-api-fetch'
import { NotificationAPI as NotificationAPIImpl } from './notification-api-fetch'
import { SearchAPI as SearchAPIImpl } from './search-api-fetch'
import { GroupAPI as GroupAPIImpl } from './group-api-fetch'
import { TimelineAPI as TimelineAPIImpl } from './timeline-api-fetch'
import { PlanAPI as PlanAPIImpl } from './plan-api-fetch'
import { BillingAPI as BillingAPIImpl } from './billing-api-fetch'
import { ReportAPI as ReportAPIImpl } from './report-api-fetch'
import { StickerAPI as StickerAPIImpl } from './sticker-api-fetch'
import { AuthAPI as AuthAPIImpl } from './auth-api-fetch'
import type * as dummy_api_types from './dummy-api'
import { is_dummy_api_mode, is_history_enabled } from '../utils/config'
// .envのVITE_ENDPOINTに値が入っていない場合 is_dummy_api_mode === true
// VITE_ENDPOINTが設定されている場合はバンドルサイズ削減のためdummy-apiを動的インポートのみで使用
const dummy_api = is_dummy_api_mode ? await import('./dummy-api') : null
if (dummy_api && is_history_enabled) {
  const {bootstrapHistoryFixtures} = await import('./dummy-api/history')
  bootstrapHistoryFixtures()
}

export type OpenAPIError = { code: number, message: string }
export type OpenAPIAwaitedResponse<T> = {
  data: T
  error?: never
  response: Response
} | {
  data?: never
  error: OpenAPIError
  response: Response
}
export type OpenAPIResponse<T> = Promise<OpenAPIAwaitedResponse<T>>
export type OpenAPIResponseData<T extends OpenAPIResponse<unknown>> = T extends OpenAPIResponse<infer U> ? U : never
export type OpenAPIVoidAwaitedResponse = {
  data?: never
  error?: never
  response: Response
} | {
  data?: never
  error: OpenAPIError
  response: Response
}
export type OpenAPIVoidResponse = Promise<OpenAPIVoidAwaitedResponse>

/** OpenAPIResponseのdataを変換する関数
 * エラーがある場合はそのまま返す
 */
export const responseDataConverter = <T, R>(res: OpenAPIResponse<NonNullable<T>>, changer: (data: NonNullable<T>)=> R): OpenAPIResponse<R> =>
  res.then(({data, error, response}) => {
    return error ?
      {error, response}
    :
      {
        data: changer(data!),
        response
      }
  })

export type PhotoAPIInterface = Omit<typeof dummy_api_types.PhotoAPI, 'photos' | 'my_photos' | 'trash_photos'>
export type UserAPIInterface = Omit<typeof dummy_api_types.UserAPI, 'me'>
export type AlbumAPIInterface = Omit<typeof dummy_api_types.AlbumAPI, 'albums'>
export type FavoriteAPIInterface = Omit<typeof dummy_api_types.FavoriteAPI, 'favorite'>
export type ShareAPIInterface = Omit<typeof dummy_api_types.ShareAPI, 'shares' | 'me_registerd_password_share_ids' | 'isReadable' | 'isAppendable'>
export type DMAPIInterface = Omit<typeof dummy_api_types.DMAPI, 'dms'>
export type SettingAPIInterface = Omit<typeof dummy_api_types.SettingAPI, 'clients' | 'notification_settings'>
export type NotificationAPIInterface = Omit<typeof dummy_api_types.NotificationAPI, 'notifications'>
export type SearchAPIInterface = typeof dummy_api_types.SearchAPI
export type GroupAPIInterface = Omit<typeof dummy_api_types.GroupAPI, 'groups'>
export type TimelineAPIInterface = Omit<typeof dummy_api_types.TimelineAPI, 'me_timeline' | 'main_timeline'>
export type PlanAPIInterface = typeof dummy_api_types.PlanAPI
export type BillingAPIInterface = Omit<typeof dummy_api_types.BillingAPI, never>
export type ReportAPIInterface = typeof dummy_api_types.ReportAPI
export type StickerAPIInterface = typeof dummy_api_types.StickerAPI
export type HistoryAPIInterface = typeof dummy_api_types.HistoryAPI
export type WorldAPIInterface = typeof dummy_api_types.WorldAPI
export type AuthAPIInterface = typeof dummy_api_types.AuthAPI

const historyUnavailable = (): OpenAPIResponse<never> => Promise.resolve({
  error: {code: 404, message: 'History is not connected'}, response: new Response(null, {status: 404}),
})
const worldDirectoryUnavailable = (): OpenAPIResponse<never> => Promise.resolve({
  error: {code: 404, message: 'World directory is not connected'}, response: new Response(null, {status: 404}),
})
export const HistoryAPI: HistoryAPIInterface = dummy_api?.HistoryAPI ?? {
  listUserHistoryIndex: historyUnavailable,
  listUserHistories: historyUnavailable,
  getUserHistory: historyUnavailable,
  listWorldVisitIndex: historyUnavailable,
  listWorldVisits: historyUnavailable,
  getWorldVisit: historyUnavailable,
  getWorldVisitEncounter: historyUnavailable,
}
export const WorldAPI: WorldAPIInterface = dummy_api?.WorldAPI ?? {searchAllWorlds: worldDirectoryUnavailable}

export const PhotoAPI: PhotoAPIInterface = is_dummy_api_mode ? dummy_api!.PhotoAPI : PhotoAPIImpl
export const UserAPI: UserAPIInterface = is_dummy_api_mode ? dummy_api!.UserAPI : UserAPIImpl
export const AlbumAPI: AlbumAPIInterface = is_dummy_api_mode ? dummy_api!.AlbumAPI : AlbumAPIImpl
export const FavoriteAPI: FavoriteAPIInterface = is_dummy_api_mode ? dummy_api!.FavoriteAPI : FavoriteAPIImpl
export const ShareAPI: ShareAPIInterface = is_dummy_api_mode ? dummy_api!.ShareAPI : ShareAPIImpl
export const DMAPI: DMAPIInterface = is_dummy_api_mode ? dummy_api!.DMAPI : DMAPIImpl
export const SettingAPI: SettingAPIInterface = is_dummy_api_mode ? dummy_api!.SettingAPI : SettingAPIImpl
export const NotificationAPI: NotificationAPIInterface = is_dummy_api_mode ? dummy_api!.NotificationAPI : NotificationAPIImpl
export const SearchAPI: SearchAPIInterface = is_dummy_api_mode ? dummy_api!.SearchAPI : SearchAPIImpl
export const GroupAPI: GroupAPIInterface = is_dummy_api_mode ? dummy_api!.GroupAPI : GroupAPIImpl
export const TimelineAPI: TimelineAPIInterface = is_dummy_api_mode ? dummy_api!.TimelineAPI : TimelineAPIImpl
export const PlanAPI: PlanAPIInterface = is_dummy_api_mode ? dummy_api!.PlanAPI : PlanAPIImpl
export const BillingAPI: BillingAPIInterface = is_dummy_api_mode ? dummy_api!.BillingAPI : BillingAPIImpl
export const ReportAPI: ReportAPIInterface = is_dummy_api_mode ? dummy_api!.ReportAPI : ReportAPIImpl
export const StickerAPI: StickerAPIInterface = is_dummy_api_mode ? dummy_api!.StickerAPI : StickerAPIImpl
export const AuthAPI: AuthAPIInterface = is_dummy_api_mode ? dummy_api!.AuthAPI : AuthAPIImpl
